import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

@Component({
  selector: 'app-market-share-progress',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './market-share-progress.component.html',
  styleUrls: ['./market-share-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketShareProgressComponent {
  private _data: any;

  @Input()
  set data(value: any) {
    this._data = value;
    this.rebuildViewModel();
  }

  get data(): any {
    return this._data;
  }

  title = 'Cummins Market Share';
  valueLabel = '0%';
  description = 'Share of Market';
  details: string[] = [];
  showDivider = false;
  fillStyle: Record<string, string> = {};
  markerStyle: Record<string, string> = {};
  scaleStops: Array<{ label: string; left: number; edgeClass: 'start' | 'middle' | 'end' }> = [];
  segments: Array<{ left: number; width: number; color: string }> = [];
  hasScaleStops = false;
  showScaleLabels = true;
  showSegments = true;
  showPrimaryBar = true;
  showEdgeLabels = false;
  showMarkerValueLabel = false;
  hideFooterValue = false;
  hideFooterDescription = false;
  startLabel = '';
  endLabel = '';
  markerValueLabelStyle: Record<string, string> = {};

  get min(): number {
    const minValue = Number(this.data?.min ?? 0);
    return Number.isFinite(minValue) ? minValue : 0;
  }

  get value(): number {
    const numberValue = Number(this.data?.value ?? 2);
    return Number.isFinite(numberValue) ? numberValue : 0;
  }

  get max(): number {
    const maxValue = Number(this.data?.max ?? 100);
    return Number.isFinite(maxValue) && maxValue > this.min ? maxValue : 100;
  }

  get progress(): number {
    const range = this.max - this.min;
    if (!Number.isFinite(range) || range <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(((this.value - this.min) / range) * 100, 100));
  }

  private toPosition(value: number): number {
    const range = this.max - this.min;
    if (!Number.isFinite(range) || range <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(((value - this.min) / range) * 100, 100));
  }

  private formatStopLabel(value: number): string {
    return `${Number(value.toFixed(2))}%`;
  }

  private rebuildViewModel(): void {
    this.title = this.data?.title || 'Cummins Market Share';
    this.valueLabel = this.data?.valueLabel || `${this.value}%`;
    this.description = this.data?.description || 'Share of Market';
    this.details = Array.isArray(this.data?.details) ? this.data.details : [];
    this.showDivider = Boolean(this.data?.showDivider || this.details.length);
    this.fillStyle = {
      width: `${this.progress}%`,
      background: this.data?.fillColor || this.data?.markerColor || '#e30613',
    };
    this.markerStyle = {
      left: `${this.progress}%`,
      color: this.data?.markerColor || '#e30613',
    };
    this.markerValueLabelStyle = {
      left: `${this.progress}%`,
    };
    this.scaleStops = this.buildScaleStops();
    this.segments = this.buildSegments(this.scaleStops);
    this.showScaleLabels = this.data?.showScaleLabels !== false;
    this.showSegments = this.data?.showSegments !== false;
    this.showPrimaryBar = this.data?.showPrimaryBar !== false;
    this.startLabel = String(this.data?.startLabel ?? '').trim();
    this.endLabel = String(this.data?.endLabel ?? '').trim();
    this.showEdgeLabels = this.data?.showEdgeLabels === true;
    this.showMarkerValueLabel = this.data?.showMarkerValueLabel === true;
    this.hideFooterValue = this.data?.hideFooterValue === true;
    this.hideFooterDescription = this.data?.hideFooterDescription === true;
    this.hasScaleStops = this.showScaleLabels && this.scaleStops.length > 0;
  }

  private buildScaleStops(): Array<{ label: string; left: number; edgeClass: 'start' | 'middle' | 'end' }> {
    const configuredStops = Array.isArray(this.data?.scaleStops) ? this.data.scaleStops : [];
    const rawStops = configuredStops.length
      ? configuredStops
      : [
          { value: this.min, label: this.formatStopLabel(this.min) },
          { value: this.max, label: this.formatStopLabel(this.max) },
        ];

    return rawStops
      .map((stop: any, index: number, array: any[]) => {
        const value = Number(stop?.value);
        if (!Number.isFinite(value)) {
          return null;
        }

        const edgeClass: 'start' | 'middle' | 'end' =
          index === 0 ? 'start' : index === array.length - 1 ? 'end' : 'middle';

        return {
          label: String(stop?.label ?? this.formatStopLabel(value)),
          left: this.toPosition(value),
          edgeClass,
        };
      })
      .filter(
        (
          stop: { label: string; left: number; edgeClass: 'start' | 'middle' | 'end' } | null,
        ): stop is { label: string; left: number; edgeClass: 'start' | 'middle' | 'end' } => Boolean(stop),
      );
  }

  private buildSegments(
    stops: Array<{ label: string; left: number; edgeClass: 'start' | 'middle' | 'end' }>,
  ): Array<{ left: number; width: number; color: string }> {
    if (stops.length < 2) {
      return [];
    }

    const configuredRangeColors = Array.isArray(this.data?.rangeColors) ? this.data.rangeColors : [];
    const fallbackFilled = this.data?.fillColor || this.data?.markerColor || '#e30613';
    const fallbackEmpty = this.data?.trackColor || '#e3e7ed';

    return stops.slice(0, -1).map((stop, index) => ({
      left: stop.left,
      width: Math.max(0, stops[index + 1].left - stop.left),
      color: configuredRangeColors[index] || (index === 0 ? fallbackFilled : fallbackEmpty),
    }));
  }
}
